<template>
  <div class="login-modal">
    <!-- 添加關閉按鈕 -->
    <div class="form-container">
      <button @click="closeModal" class="close-btn">&times;</button>
      
      <!-- 統一認證系統 -->
      <div v-if="currentView === 'login'" class="form-container">
        <h2>統一認證系統</h2>
        <div class="unified-auth-message">
          <div class="message-icon">
            <i class="fas fa-info-circle"></i>
          </div>
          <div class="message-content">
            <h3>歌點通已整合到生活通系統</h3>
            <p>請使用生活通系統進行登入，享受統一的用戶體驗。</p>
            <div class="redirect-info">
              <p><strong>系統整合優勢：</strong></p>
              <ul>
                <li>✅ 統一帳號管理</li>
                <li>✅ 跨系統點數共享</li>
                <li>✅ 簡化登入流程</li>
                <li>✅ 更好的安全性</li>
              </ul>
            </div>
          </div>
          <button @click="redirectToMainSystem" class="btn-primary">
            <i class="fas fa-external-link-alt"></i>
            前往生活通登入
          </button>
        </div>
        <div class="links">
          <a @click="currentView = 'register'">註冊新帳號</a>
          <a @click="currentView = 'forgotPassword'">忘記密碼</a>
        </div>
      </div>

      <!-- 註冊表單 -->
      <div v-if="currentView === 'register'" class="form-container">
        <h2>註冊</h2>
        <form @submit.prevent="handleRegister">
          <div class="form-group">
            <input
              v-model="registerForm.username"
              type="text"
              placeholder="手機號碼或電子郵件"
              @input="validateUsername"
              required
            >
            <span class="input-type">{{ usernameType }}</span>
            <div class="error-message" v-if="registerForm.usernameError">
              {{ registerForm.usernameError }}
            </div>
          </div>
          <div class="form-group" v-if="registerForm.isValidUsername">
            <input
              v-model="registerForm.password"
              type="password"
              placeholder="密碼"
              @input="validatePassword"
              required
              autocomplete="current-password"
            >
            <div class="error-message" v-if="registerForm.passwordError">
              {{ registerForm.passwordError }}
            </div>
          </div>
          <div class="form-group" v-if="showVerificationCode">
            <input
              v-model="registerForm.verificationCode"
              type="text"
              placeholder="驗證碼"
              required
            >
            <button 
              type="button" 
              @click="sendVerificationCode" 
              :disabled="countdown > 0"
              class="btn-secondary"
            >
              {{ countdown > 0 ? `重新發送 (${countdown}s)` : '獲取驗證碼' }}
            </button>
          </div>
          <div class="error-message" v-if="errorMessage">{{ errorMessage }}</div>
          <button type="submit" class="btn-primary" :disabled="!canSubmitRegister">
            註冊
          </button>
        </form>
        <div class="links">
          <a @click="currentView = 'login'">返回登入</a>
        </div>
      </div>

      <!-- 忘記密碼表單 -->
      <div v-if="currentView === 'forgotPassword'" class="form-container">
        <h2>重設密碼</h2>
        <form @submit.prevent="handleResetPassword">
          <div class="form-group" v-if="!resetForm.verified">
            <input
              v-model="resetForm.username"
              type="text"
              placeholder="手機號碼或電子郵件"
              @blur="validateResetUsername"
              required
            >
            <div class="error-message" v-if="resetForm.usernameError">
              {{ resetForm.usernameError }}
            </div>
          </div>
          <div class="form-group" v-if="resetForm.isValidUsername && !resetForm.verified">
            <input
              v-model="resetForm.verificationCode"
              type="text"
              placeholder="驗證碼"
              required
            >
            <button 
              type="button" 
              @click="sendResetVerificationCode" 
              :disabled="resetCooldown > 0 || !resetForm.isValidUsername"
              class="btn-secondary"
              style="margin-left: 0.5rem; z-index: 9999999 !important; position: relative !important;"
            >
              {{ resetCooldown > 0 ? `重新發送 (${resetCooldown}s)` : '發送驗證碼' }}
            </button>
          </div>
          <template v-if="resetForm.verified">
            <div class="form-group">
              <input
                v-model="resetForm.newPassword"
                type="password"
                placeholder="新密碼"
                @input="validateNewPassword"
                required
                autocomplete="current-password"
              >
            </div>
            <div class="form-group">
              <input
                v-model="resetForm.confirmPassword"
                type="password"
                placeholder="確認新密碼"
                required
                autocomplete="current-password"
              >
            </div>
            <div class="error-message" v-if="resetForm.passwordError">
              {{ resetForm.passwordError }}
            </div>
          </template>
          <div class="error-message" v-if="errorMessage">{{ errorMessage }}</div>
          <button 
            type="submit" 
            class="btn-primary"
            :disabled="!canSubmitReset"
          >
            {{ resetForm.verified ? '更新密碼' : '驗證' }}
          </button>
        </form>
        <div class="links">
          <a @click="currentView = 'login'">返回登入</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineEmits } from 'vue';
import axios from 'axios';
import { useUserStore } from '@/store/useUserStore';
import { useRouter } from 'vue-router';
import { getCookie } from '../utils/csrf.js';  // 導入 getCookie 函數

const userStore = useUserStore();
const router = useRouter();

// 定義事件
const emit = defineEmits(['login-success', 'close-modal']);

// 視圖狀態
const currentView = ref('login');
const errorMessage = ref('');

// 登入表單
const loginForm = ref({
  username: '',
  password: ''
});

// 註冊表單
const registerForm = ref({
  username: '',
  password: '',
  verificationCode: '',
  usernameError: '',
  passwordError: '',
  isValidUsername: false
});

// 重設密碼表單
const resetForm = ref({
  username: '',
  verificationCode: '',
  newPassword: '',
  confirmPassword: '',
  usernameError: '',
  passwordError: '',
  isValidUsername: false,
  verified: false
});

// 添加調試信息
console.log('resetForm 初始化:', resetForm.value);

// 重定向到主系統
const redirectToMainSystem = () => {
  console.log('重定向到主系統進行登入')
  // 重定向到主系統的登入頁面
  window.location.href = '/login'
}

// 驗證碼相關
const countdown = ref(0);
const countdownInterval = ref(null);
const countdownDuration = ref(180); // 修改為 180 秒
const showVerificationCode = ref(false);

// 用戶名類型判斷
const usernameType = ref('');

// 添加密碼顯示狀態控制
const showPassword = ref(false);

// 表單提交條件計算
const canSubmitRegister = computed(() => {
  return registerForm.value.isValidUsername &&
         registerForm.value.password &&
         !registerForm.value.passwordError &&
         (!showVerificationCode.value || registerForm.value.verificationCode);
});

const canSubmitReset = computed(() => {
  const result = {
    verified: resetForm.value.verified,
    isValidUsername: resetForm.value.isValidUsername,
    verificationCode: resetForm.value.verificationCode,
    newPassword: resetForm.value.newPassword,
    confirmPassword: resetForm.value.confirmPassword,
    passwordError: resetForm.value.passwordError
  };
  
  console.log('canSubmitReset 計算:', result);
  
  if (!resetForm.value.verified) {
    const canSubmit = resetForm.value.isValidUsername && resetForm.value.verificationCode;
    console.log('未驗證狀態，可以提交:', canSubmit);
    return canSubmit;
  }
  
  const canSubmit = resetForm.value.newPassword &&
         resetForm.value.confirmPassword &&
         !resetForm.value.passwordError;
  console.log('已驗證狀態，可以提交:', canSubmit);
  return canSubmit;
});

// 驗證用戶名格式
const validateUsername = () => {
  const username = registerForm.value.username;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^09\d{8}$/;

  if (emailRegex.test(username)) {
    usernameType.value = '電子郵件';
    registerForm.value.isValidUsername = true;
    registerForm.value.usernameError = '';
  } else if (phoneRegex.test(username)) {
    usernameType.value = '手機號碼';
    registerForm.value.isValidUsername = true;
    registerForm.value.usernameError = '';
  } else {
    usernameType.value = '';
    registerForm.value.isValidUsername = false;
    registerForm.value.usernameError = '請輸入有效的手機號碼或電子郵件';
  }
};

// 驗證重設密碼用戶名
const validateResetUsername = () => {
  const username = resetForm.value.username;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^09\d{8}$/;

  console.log('驗證重設密碼用戶名:', {
    username: username,
    isEmail: emailRegex.test(username),
    isPhone: phoneRegex.test(username),
    resetFormValue: resetForm.value
  });

  if (emailRegex.test(username) || phoneRegex.test(username)) {
    resetForm.value.isValidUsername = true;
    resetForm.value.usernameError = '';
    console.log('用戶名驗證成功，設置 isValidUsername 為 true');
    console.log('更新後的 resetForm:', resetForm.value);
  } else {
    resetForm.value.isValidUsername = false;
    resetForm.value.usernameError = '請輸入有效的手機號碼或電子郵件';
    console.log('用戶名驗證失敗，設置 isValidUsername 為 false');
    console.log('更新後的 resetForm:', resetForm.value);
  }
};

// 驗證密碼強度
const validatePassword = () => {
  const password = registerForm.value.password;
  if (password.length < 8) {
    registerForm.value.passwordError = '密碼長度至少需要8個字符';
    return;
  }
  if (!/[A-Z]/.test(password)) {
    registerForm.value.passwordError = '密碼需要包含至少一個大寫字母';
    return;
  }
  if (!/[a-z]/.test(password)) {
    registerForm.value.passwordError = '密碼需要包含至少一個小寫字母';
    return;
  }
  if (!/[0-9]/.test(password)) {
    registerForm.value.passwordError = '密碼需要包含至少一個數字';
    return;
  }
  registerForm.value.passwordError = '';
};

// 驗證新密碼
const validateNewPassword = () => {
  const { newPassword, confirmPassword } = resetForm.value;
  if (newPassword.length < 8) {
    resetForm.value.passwordError = '密碼長度至少需要8個字符';
    return;
  }
  if (confirmPassword && newPassword !== confirmPassword) {
    resetForm.value.passwordError = '兩次輸入的密碼不一致';
    return;
  }
  resetForm.value.passwordError = '';
};

// 更新 computed 邏輯
watch(() => registerForm.value.isValidUsername, (isValid) => {
  if (isValid) {
    // 只在用戶名有效時顯示驗證碼輸入框
    showVerificationCode.value = true;
  }
});

// 修改發送驗證碼函數
const sendVerificationCode = async () => {
  try {
    console.log('發送驗證碼...');
    errorMessage.value = '';
    
    // 判斷用戶名類型
    const username = registerForm.value.username;
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username);
    const isPhone = /^09\d{8}$/.test(username);
    
    if (!isEmail && !isPhone) {
      errorMessage.value = '請輸入有效的電子郵件或手機號碼';
      return;
    }
    
    // 準備請求參數
    let requestData = {
      username: username,
      type: 'register'
    };
    
    // 如果是手機號碼，添加三竹簡訊 API 所需參數
    if (isPhone) {
      // 生成6位數驗證碼
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      
      // 添加三竹簡訊 API 參數
      requestData = {
        ...requestData,
        isMitake: true, // 標記使用三竹簡訊 API
        code: code, // 前端生成的驗證碼
        mitakeParams: {
          username: import.meta.env.VITE_MITAKE_USERNAME || 'username',
          password: import.meta.env.VITE_MITAKE_PASSWORD || 'password',
          dstaddr: username,
          smbody: `Your verification code is: ${code}. Valid for 3 minutes. Do not share with others.`,
          encoding: 'ASCII',  // 使用 ASCII 編碼
          clientid: new Date().getTime().toString(),
        }
      };
    }
    
    // 發送請求
    const response = await axios.post('/api/send-verification-code/', requestData);
    
    console.log('驗證碼發送響應:', response.data);
    
    if (response.data.success) {
      console.log('驗證碼發送成功');
      showVerificationCode.value = true;
      countdown.value = countdownDuration.value;
      countdownInterval.value = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
          clearInterval(countdownInterval.value);
          countdownInterval.value = null;
        }
      }, 1000);
      
      // 添加提示信息，更明確地指出驗證碼發送位置
      if (isEmail) {
        errorMessage.value = `驗證碼已發送至郵箱 ${username}，請查收！（如未收到，請檢查垃圾郵件）`;
      } else {
        errorMessage.value = `驗證碼已發送至手機 ${username}，請查收！`;
      }
      
      // 在開發環境中顯示控制台提示
      if (import.meta.env.DEV) {
        console.log('請查看後端控制台輸出，獲取測試用的驗證碼');
      }
    } else {
      console.log('驗證碼發送失敗:', response.data.message);
      errorMessage.value = response.data.message || '驗證碼發送失敗，請稍後重試';
    }
  } catch (error) {
    console.error('驗證碼發送出錯:', error);
    errorMessage.value = '驗證碼發送失敗，請稍後重試';
  }
};

// 將 resetCooldown 從布林值改為數字
const resetCooldown = ref(0); // 初始值為 0

// 修改發送重設密碼驗證碼函數
const sendResetVerificationCode = async () => {
  try {
    console.log('發送重設密碼驗證碼...');
    errorMessage.value = '';
    
    // 判斷用戶名類型
    const username = resetForm.value.username;
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username);
    const isPhone = /^09\d{8}$/.test(username);
    
    if (!isEmail && !isPhone) {
      errorMessage.value = '請輸入有效的電子郵件或手機號碼';
      return;
    }
    
    // 準備請求參數
    let requestData = {
      username: username,
      type: 'reset'
    };
    
    // 如果是手機號碼，添加三竹簡訊 API 所需參數
    if (isPhone) {
      // 生成6位數驗證碼
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      
      // 添加三竹簡訊 API 參數
      requestData = {
        ...requestData,
        isMitake: true, // 標記使用三竹簡訊 API
        code: code, // 前端生成的驗證碼
        mitakeParams: {
          username: import.meta.env.VITE_MITAKE_USERNAME || 'username',
          password: import.meta.env.VITE_MITAKE_PASSWORD || 'password',
          dstaddr: username,
          smbody: `Your password reset code is: ${code}. Valid for 3 minutes. Do not share with others.`,
          encoding: 'ASCII',  // 使用 ASCII 編碼
          clientid: new Date().getTime().toString(),
        }
      };
    }
    
    // 發送請求
    const response = await axios.post('/api/send-verification-code/', requestData);
    
    console.log('重設密碼驗證碼發送響應:', response.data);
    
    if (response.data.success) {
      console.log('重設密碼驗證碼發送成功');
      resetCooldown.value = countdownDuration.value;
      const resetInterval = setInterval(() => {
        resetCooldown.value--;
        if (resetCooldown.value <= 0) {
          clearInterval(resetInterval);
        }
      }, 1000);
      
      // 添加提示信息，更明確地指出驗證碼發送位置
      if (isEmail) {
        errorMessage.value = `驗證碼已發送至郵箱 ${username}，請查收！（如未收到，請檢查垃圾郵件）`;
      } else {
        errorMessage.value = `驗證碼已發送至手機 ${username}，請查收！`;
      }
      
      // 在開發環境中顯示控制台提示
      if (import.meta.env.DEV) {
        console.log('請查看後端控制台輸出，獲取測試用的驗證碼');
      }
    } else {
      console.log('重設密碼驗證碼發送失敗:', response.data.message);
      errorMessage.value = response.data.message || '驗證碼發送失敗，請稍後重試';
    }
  } catch (error) {
    console.error('重設密碼驗證碼發送出錯:', error);
    errorMessage.value = '驗證碼發送失敗，請稍後重試';
  }
};

// 處理登入
const handleLogin = async () => {
  try {
    errorMessage.value = '';
    
    // 獲取 CSRF Token
    const csrftoken = getCookie('csrftoken');
    
    // 發送登入請求
    const response = await axios.post('/api/login/', loginForm.value, {
      headers: {
        'X-CSRFToken': csrftoken
      }
    });
    
    if (response.data.success) {
      // 使用登入響應中的用戶信息
      const user = response.data.user;
      
      // 如果響應中包含客戶 ID，獲取餘額信息
      if (user && user.customer_id) {
        try {
          const customerResponse = await axios.get(`/api/customer/${user.customer_id}/balancepoints/`);
          
          // 更新用戶信息，包括餘額
          userStore.setUser({
            ...user,
            balance: customerResponse.data.balancepoints || 0
          });
        } catch (balanceError) {
          console.error('獲取餘額失敗:', balanceError);
          // 即使獲取餘額失敗，仍然設置用戶信息
          userStore.setUser(user);
        }
      } else {
        // 如果沒有客戶 ID，直接設置用戶信息
        userStore.setUser(user);
      }
      
      // 重定向到首頁或其他頁面
      router.push('/');
      
      // 登入成功後
      emit('login-success');
    } else {
      errorMessage.value = response.data.message || '登入失敗，請檢查您的憑證';
    }
  } catch (error) {
    console.error('登入過程出錯:', error);
    if (error.response) {
      errorMessage.value = error.response.data.message || '登入失敗，請檢查您的憑證';
    } else {
      errorMessage.value = '網絡錯誤，請稍後重試';
    }
  }
};

// 處理註冊
const handleRegister = async () => {
  try {
    console.log('開始註冊...');
    errorMessage.value = '';
    
    // 打印更多信息用於調試
    console.log('註冊信息:', {
      username: registerForm.value.username,
      password: '******', // 不打印密碼
      verificationCode: registerForm.value.verificationCode
    });
    
    // 使用相對路徑
    const response = await axios.post('/api/register/', {
      username: registerForm.value.username,
      password: registerForm.value.password,
      verificationCode: registerForm.value.verificationCode  // 確保字段名稱正確
    });
    
    console.log('註冊響應:', response.data);
    
    if (response.data.success) {
      console.log('註冊成功');
      currentView.value = 'login';
      errorMessage.value = '註冊成功，請登入';
    } else {
      console.log('註冊失敗:', response.data.message);
      
      // 如果是驗證碼問題，提供更明確的提示
      if (response.data.message.includes('驗證碼無效') || response.data.message.includes('已過期')) {
        errorMessage.value = '驗證碼無效或已過期，請重新獲取驗證碼';
        // 重置驗證碼輸入
        registerForm.value.verificationCode = '';
        // 允許重新發送
        countdown.value = 0;
      } else {
        errorMessage.value = response.data.message || '註冊失敗，請稍後重試';
      }
    }
  } catch (error) {
    console.error('註冊出錯:', error);
    if (error.response) {
      console.log('後端返回錯誤:', error.response.data);
      errorMessage.value = error.response.data.message || '註冊失敗，請稍後重試';
    } else {
      errorMessage.value = '網絡錯誤，請稍後重試';
    }
  }
};

// 處理重設密碼
const handleResetPassword = async () => {
  try {
    if (!resetForm.value.verified) {
      console.log('開始驗證驗證碼...');
      
      // 清理驗證碼中可能的空格
      const cleanCode = resetForm.value.verificationCode.trim();
      
      // 開發環境測試模式 - 任何六位數字都可以通過驗證
      console.log('檢查開發環境測試模式:', {
        isDev: import.meta.env.DEV,
        cleanCode: cleanCode,
        isValidCode: /^\d{6}$/.test(cleanCode),
        shouldUseTestMode: import.meta.env.DEV && /^\d{6}$/.test(cleanCode)
      });
      
      if (import.meta.env.DEV && /^\d{6}$/.test(cleanCode)) {
        console.log('開發環境測試模式：驗證碼驗證成功');
        resetForm.value.verified = true;
        errorMessage.value = '驗證成功，請設置新密碼';
        return;
      }
      
      // 正常驗證流程
      console.log('發送驗證請求到 /api/verify-code/', {
        username: resetForm.value.username,
        code: cleanCode,
        type: 'reset'
      });
      
      console.log('準備發送 axios 請求...');
      console.log('axios 對象:', axios);
      
      const response = await axios.post('/api/verify-code/', {
        username: resetForm.value.username,
        code: cleanCode,
        type: 'reset'
      });
      
      console.log('axios 請求已發送，等待響應...');
      
      console.log('驗證碼驗證響應:', response.data);
      console.log('響應狀態:', response.status);
      
      if (response.data.success) {
        console.log('驗證碼驗證成功');
        resetForm.value.verified = true;
        errorMessage.value = '驗證成功，請設置新密碼';
      } else {
        console.log('驗證碼驗證失敗:', response.data.message);
        errorMessage.value = response.data.message || '驗證碼無效，請重新輸入';
        // 清空驗證碼輸入框
        resetForm.value.verificationCode = '';
      }
    } else {
      console.log('開始更新密碼...');
      // 更新密碼
      const response = await axios.post('/api/reset-password/', {
        username: resetForm.value.username,
        password: resetForm.value.newPassword
      });
      console.log('密碼更新響應:', response.data);
      
      if (response.data.success) {
        console.log('密碼更新成功');
        currentView.value = 'login';
        errorMessage.value = '密碼重設成功，請使用新密碼登入';
      } else {
        console.log('密碼更新失敗:', response.data.message);
        errorMessage.value = response.data.message || '密碼更新失敗，請稍後重試';
      }
    }
  } catch (error) {
    console.error('重設密碼過程出錯:', error);
    console.error('錯誤詳情:', {
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config
    });
    
    if (error.response) {
      console.log('後端返回錯誤:', error.response.data);
      console.log('錯誤狀態碼:', error.response.status);
      errorMessage.value = error.response.data.message || 
        (resetForm.value.verified ? '密碼更新失敗，請稍後重試' : '驗證碼驗證失敗，請檢查驗證碼');
    } else if (error.request) {
      console.error('請求未收到響應:', error.request);
      errorMessage.value = '網絡請求失敗，請檢查網絡連接';
    } else {
      console.error('請求配置錯誤:', error.message);
      errorMessage.value = '請求配置錯誤，請稍後重試';
    }
  }
};

// 關閉 Modal
const closeModal = () => {
  emit('close-modal');
};

// 添加 PDF 檔案開啟函數
const openPdf = (type) => {
  const filename = type === 'privacy' ? '隱私權政策.pdf' : '歌點通使用條款.pdf';
  const url = `/docs/${encodeURIComponent(filename)}`;
  
  // 直接在新視窗中開啟 PDF
  window.open(url, '_blank');
};

// 切換密碼顯示/隱藏
const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

// 處理 Google 登入
const handleGoogleLogin = async () => {
  try {
    errorMessage.value = '';
    console.log('開始 Google 登入流程...');
    
    // 檢測瀏覽器類型
    const isChrome = /Chrome/.test(navigator.userAgent);
    const isFirefox = /Firefox/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isEdge = /Edg/.test(navigator.userAgent);
    
    console.log('瀏覽器檢測:', {
      userAgent: navigator.userAgent,
      isChrome,
      isFirefox,
      isSafari,
      isEdge
    });
    
    // 發送請求到後端獲取 Google OAuth URL
    const response = await axios.get('/api/google/oauth/?popup=true');
    
    if (response.data.oauth_url) {
      console.log('獲取到 Google OAuth URL:', response.data.oauth_url);
      
      // 先清理之前的 OAuth 結果
      localStorage.removeItem('google_oauth_result');
      
      // Chrome 總是使用重定向方式（避免第三方 Cookie 問題）
      if (isChrome) {
        console.log('檢測到 Chrome 瀏覽器，使用直接重定向方式');
        errorMessage.value = 'Chrome 不支援彈出視窗，正在重定向到 Google 登入頁面...';
        
        // 立即重定向，不等待
        console.log('立即執行重定向到:', response.data.oauth_url);
        window.location.href = response.data.oauth_url;
        return;
      }
      
      // 其他瀏覽器嘗試彈出視窗
      console.log('非 Chrome 瀏覽器，嘗試彈出視窗方式');
      
      // 檢測瀏覽器是否支持彈出視窗
      let supportsPopup = false;
      try {
        const testPopup = window.open('', '_blank', 'width=1,height=1');
        if (testPopup && !testPopup.closed) {
          testPopup.close();
          supportsPopup = true;
        }
      } catch (e) {
        console.warn('彈出視窗測試失敗:', e);
        supportsPopup = false;
      }
      
      console.log('彈出視窗支持檢測結果:', supportsPopup);
      
      if (!supportsPopup) {
        console.log('彈出視窗不受支持，使用重定向方式');
        errorMessage.value = '瀏覽器不支援彈出視窗，正在重定向到 Google 登入頁面...';
        setTimeout(() => {
          console.log('執行重定向到:', response.data.oauth_url);
          window.location.href = response.data.oauth_url;
        }, 1500);
        return;
      }
      
      // 嘗試彈出視窗方式
      console.log('嘗試使用彈出視窗...');
      
      // 計算彈出視窗位置（居中）
      const width = 600;
      const height = 700;
      const left = Math.round((screen.width / 2) - (width / 2));
      const top = Math.round((screen.height / 2) - (height / 2));
      
      // 嘗試使用彈出視窗進行 Google OAuth 登入
      let popup;
      try {
        popup = window.open(
          response.data.oauth_url,
          'google-oauth',
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,status=no,toolbar=no,menubar=no,location=yes`
        );
      } catch (e) {
        console.error('開啟彈出視窗時出錯:', e);
        popup = null;
      }
      
      console.log('window.open 返回值:', popup);
      
      // 檢查彈出視窗是否成功開啟
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        console.log('彈出視窗被阻擋，使用直接重定向');
        errorMessage.value = '瀏覽器阻擋了彈出視窗，正在重定向到 Google 登入頁面...';
        setTimeout(() => {
          console.log('執行重定向到:', response.data.oauth_url);
          window.location.href = response.data.oauth_url;
        }, 2000);
        return;
      }
      
      console.log('彈出視窗已成功開啟');
      
      // 嘗試聚焦到彈出視窗
      try {
        popup.focus();
        console.log('彈出視窗已聚焦');
      } catch (e) {
        console.warn('無法聚焦彈出視窗:', e);
      }
      
      // 設置全域處理函數供彈出視窗調用
      window.handleGoogleOAuthResult = (data) => {
        console.log('收到 Google OAuth 結果（直接調用）:', data);
        if (data.type === 'GOOGLE_OAUTH_SUCCESS') {
          handleGoogleLoginSuccess(data);
        } else if (data.type === 'GOOGLE_OAUTH_ERROR') {
          errorMessage.value = data.error || 'Google 登入失敗';
        }
      };
      
      // 監聽 postMessage 事件
      const messageListener = (event) => {
        console.log('收到 postMessage:', event.data, '來源:', event.origin);
        
        if (event.data && event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
          console.log('收到 Google OAuth 成功消息（postMessage）', event.data);
          handleGoogleLoginSuccess(event.data);
          window.removeEventListener('message', messageListener);
        } else if (event.data && event.data.type === 'GOOGLE_OAUTH_ERROR') {
          console.error('收到 Google OAuth 錯誤消息（postMessage）', event.data);
          errorMessage.value = event.data.error || 'Google 登入失敗';
          window.removeEventListener('message', messageListener);
        }
      };
      
      window.addEventListener('message', messageListener);
      
      // 監聽彈出視窗關閉
      let checkCount = 0;
      const checkClosed = setInterval(() => {
        checkCount++;
        
        try {
          // 檢查彈出視窗是否關閉
          if (popup.closed) {
            clearInterval(checkClosed);
            console.log('彈出視窗已關閉，檢查登入狀態');
            
            // 移除事件監聽器
            window.removeEventListener('message', messageListener);
            delete window.handleGoogleOAuthResult;
            
            // 延遲檢查 localStorage，確保有足夠時間寫入
            setTimeout(() => {
              checkGoogleLoginCallback();
            }, 500);
          }
        } catch (e) {
          // 可能是跨域錯誤，忽略
          console.warn('檢查彈出視窗狀態時出錯:', e);
        }
        
        // 如果檢查超過 300 次（2.5 分鐘），停止檢查
        if (checkCount > 300) {
          clearInterval(checkClosed);
          console.log('彈出視窗檢查超時');
          window.removeEventListener('message', messageListener);
          delete window.handleGoogleOAuthResult;
          
          // 如果超時仍未關閉，嘗試關閉彈出視窗
          try {
            if (!popup.closed) {
              popup.close();
            }
          } catch (e) {
            console.warn('關閉超時彈出視窗時出錯:', e);
          }
        }
      }, 500);
      
    } else {
      console.error('後端響應中沒有 oauth_url:', response.data);
      errorMessage.value = '無法獲取 Google 登入 URL，請稍後重試';
    }
  } catch (error) {
    console.error('Google 登入過程出錯:', error);
    if (error.response) {
      console.error('後端響應錯誤:', error.response.data);
      errorMessage.value = error.response.data.error || 'Google 登入失敗，請稍後重試';
    } else if (error.request) {
      console.error('請求沒有響應:', error.request);
      errorMessage.value = '網絡請求失敗，請檢查網絡連接';
    } else {
      console.error('請求配置錯誤:', error.message);
      errorMessage.value = '請求配置錯誤，請稍後重試';
    }
  }
};

// 開啟 Google OAuth 彈出視窗
const openGoogleOAuthPopup = (oauthUrl) => {
  console.log('準備開啟彈出視窗，URL:', oauthUrl);
  
  // 添加 popup 參數到 URL
  const popupUrl = oauthUrl.includes('?') ? 
    `${oauthUrl}&popup=true` : 
    `${oauthUrl}?popup=true`;
  
  console.log('彈出視窗 URL:', popupUrl);
  
  // 設定彈出視窗參數 - 增加尺寸以確保能夠正確顯示
  const popup = window.open(
    popupUrl,
    'google-oauth',
    'width=600,height=700,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=yes,status=no,left=' + 
    ((screen.width / 2) - 300) + ',top=' + ((screen.height / 2) - 350)
  );
  
  console.log('彈出視窗對象:', popup);
  
  if (!popup || popup.closed || typeof popup.closed == 'undefined') {
    console.log('彈出視窗被阻擋，使用直接重定向');
    errorMessage.value = '彈出視窗被瀏覽器阻擋，正在重定向到 Google 登入頁面...';
    // 延遲一秒後重定向，讓用戶看到提示
    setTimeout(() => {
      window.location.href = popupUrl;
    }, 1000);
    return;
  }
  
  // 嘗試聚焦到彈出視窗
  try {
    popup.focus();
  } catch (e) {
    console.warn('無法聚焦彈出視窗:', e);
  }
  
  // 監聽彈出視窗關閉事件
  const checkClosed = setInterval(() => {
    try {
      if (popup.closed) {
        clearInterval(checkClosed);
        console.log('彈出視窗已關閉');
        // 檢查是否登入成功
        checkGoogleLoginCallback();
      }
    } catch (e) {
      // 忽略跨域錯誤
      console.warn('檢查彈出視窗狀態時出錯:', e);
    }
  }, 1000);
  
  // 監聽來自彈出視窗的消息
  const messageListener = (event) => {
    console.log('收到消息:', event.data, '來源:', event.origin);
    
    // 檢查來源 - 允許來自同一域名或 Google 域名
    const allowedOrigins = [
      window.location.origin,
      'https://accounts.google.com',
      'https://singsong.cc'
    ];
    
    if (!allowedOrigins.includes(event.origin)) {
      console.warn('不信任的消息來源:', event.origin);
      return;
    }
    
    if (event.data && event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
      console.log('收到 Google OAuth 成功消息', event.data);
      
      // 關閉彈出視窗
      try {
        if (!popup.closed) {
          popup.close();
        }
      } catch (e) {
        console.warn('關閉彈出視窗時出錯:', e);
      }
      clearInterval(checkClosed);
      
      // 處理登入成功
      handleGoogleLoginSuccess(event.data);
      
      // 移除事件監聽器
      window.removeEventListener('message', messageListener);
    } else if (event.data && event.data.type === 'GOOGLE_OAUTH_ERROR') {
      console.error('收到 Google OAuth 錯誤消息', event.data);
      
      // 關閉彈出視窗
      try {
        if (!popup.closed) {
          popup.close();
        }
      } catch (e) {
        console.warn('關閉彈出視窗時出錯:', e);
      }
      clearInterval(checkClosed);
      
      // 顯示錯誤消息
      errorMessage.value = event.data.error || 'Google 登入失敗';
      
      // 移除事件監聽器
      window.removeEventListener('message', messageListener);
    }
  };
  
  window.addEventListener('message', messageListener);
  
  // 設定超時處理
  setTimeout(() => {
    try {
      if (!popup.closed) {
        popup.close();
        clearInterval(checkClosed);
        window.removeEventListener('message', messageListener);
        console.log('Google OAuth 登入超時');
        errorMessage.value = 'Google 登入超時，請重試';
      }
    } catch (e) {
      console.warn('超時處理時出錯:', e);
    }
  }, 300000); // 5分鐘超時
};


  // 處理 Google 登入成功
const handleGoogleLoginSuccess = (data) => {
  console.log('處理 Google 登入成功', data);
  
  // 設置用戶信息到 store
  userStore.setUser({
    customer_id: data.customer_id,
    username: data.username,
    balancepoints: parseInt(data.balancepoints) || 0,
    token: data.token
  });
  
  // 觸發登入成功事件
  emit('login-success');
  
  // 關閉登入模態框
  emit('close-modal');
  
  // 顯示成功消息
  if (data.action === 'register') {
    console.log('Google 註冊成功');
  } else {
    console.log('Google 登入成功');
  }
};

// 檢查 URL 參數以處理 Google 登入回調
const checkGoogleLoginCallback = () => {
  console.log('檢查 Google 登入回調');
  
  // 首先檢查 localStorage 中是否有 OAuth 結果（彈出視窗備用方案）
  const localStorageResult = localStorage.getItem('google_oauth_result');
  if (localStorageResult) {
    try {
      const result = JSON.parse(localStorageResult);
      console.log('從 localStorage 獲取 Google OAuth 結果:', result);
      
      if (result.success) {
        // 清理 localStorage
        localStorage.removeItem('google_oauth_result');
        
        // 處理登入成功
        handleGoogleLoginSuccess({
          customer_id: result.customer_id,
          username: result.username,
          balancepoints: result.balancepoints,
          token: result.token,
          action: result.action
        });
        return;
      }
    } catch (e) {
      console.error('解析 localStorage 中的 OAuth 結果失敗:', e);
      localStorage.removeItem('google_oauth_result');
    }
  }
  
  // 檢查 URL 參數
  const urlParams = new URLSearchParams(window.location.search);
  const loginSuccess = urlParams.get('login_success');
  const token = urlParams.get('token');
  const customer_id = urlParams.get('customer_id');
  const username = urlParams.get('username');
  const balancepoints = urlParams.get('balancepoints');
  const action = urlParams.get('action');
  const error = urlParams.get('error');
  
  console.log('URL 參數檢查:', {
    loginSuccess,
    token: !!token,
    customer_id,
    username,
    balancepoints,
    action,
    error
  });
  
  if (error) {
    console.error('Google 登入錯誤:', error);
    errorMessage.value = decodeURIComponent(error);
    // 清理 URL 參數
    window.history.replaceState({}, document.title, window.location.pathname);
    return;
  }
  
  if (loginSuccess === 'true' && token && customer_id && username) {
    console.log('Google 登入成功，處理回調');
    
    const userData = {
      customer_id: customer_id,
      username: username,
      balancepoints: parseInt(balancepoints) || 0,
      token: token
    };
    
    console.log('login.vue: 即將設置用戶數據:', userData);
    
    // 設置用戶信息到 store
    userStore.setUser(userData);
    
    console.log('login.vue: userStore.setUser() 完成');
    console.log('login.vue: 當前 userStore 狀態:', {
      isLoggedIn: userStore.isLoggedIn,
      customId: userStore.getCustomId,
      username: userStore.getUsername,
      balancepoints: userStore.getBalancePoints
    });
    
    // 強制觸發全局狀態更新事件
    window.dispatchEvent(new CustomEvent('user-login-success', { 
      detail: userData 
    }));
    console.log('login.vue: 已觸發 user-login-success 事件');
    
    // 向父組件發送全局登入事件
    window.dispatchEvent(new CustomEvent('global-user-login', { 
      detail: userData 
    }));
    console.log('login.vue: 已觸發 global-user-login 事件');
    
    // 清理 URL 參數
    window.history.replaceState({}, document.title, window.location.pathname);
    console.log('login.vue: 已清理 URL 參數');
    
    // 觸發登入成功事件
    emit('login-success');
    console.log('login.vue: 已觸發 login-success 事件');
    
    // 顯示成功消息
    if (action === 'register') {
      console.log('Google 註冊成功');
    } else {
      console.log('Google 登入成功');
    }
    
    // 延遲關閉模態框，確保狀態更新完成
    setTimeout(() => {
      emit('close-modal');
      console.log('login.vue: 已關閉登入模態框');
    }, 100);
  }
};

// 在組件掛載時檢查 Google 登入回調
import { onMounted } from 'vue';
onMounted(() => {
  checkGoogleLoginCallback();
});

</script>

<style scoped>
/* 統一認證系統樣式 */
.unified-auth-message {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  color: white;
  margin: 20px 0;
}

.message-icon {
  font-size: 48px;
  margin-bottom: 15px;
  opacity: 0.9;
}

.message-content h3 {
  margin: 0 0 10px 0;
  font-size: 24px;
  font-weight: 600;
}

.message-content p {
  margin: 0 0 20px 0;
  font-size: 16px;
  opacity: 0.9;
}

.redirect-info {
  text-align: left;
  margin: 20px 0;
  background: rgba(255, 255, 255, 0.1);
  padding: 15px;
  border-radius: 8px;
}

.redirect-info p {
  margin: 0 0 10px 0;
  font-weight: 600;
}

.redirect-info ul {
  margin: 0;
  padding-left: 20px;
}

.redirect-info li {
  margin: 5px 0;
  font-size: 14px;
}

.btn-primary {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;
}

.btn-primary:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
}

.btn-primary i {
  margin-right: 8px;
}

.login-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start; /* 改為靠上對齊 */
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 999999 !important; /* 設置比待播歌曲更高的 z-index */
  padding-top: 10vh; /* 從頂部留出 10% 的空間 */
  overflow-y: auto;
}

.form-container {
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  margin-bottom: auto; /* 確保表單在上半部 */
  position: relative;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

h2 {
  text-align: center;
  margin-bottom: 1.5rem;
  color: #333;
}

.form-group {
  margin-bottom: 1rem;
}

input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

.btn-primary {
  width: 100%;
  padding: 0.75rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 0.5rem 1rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 0.5rem;
}

.btn-secondary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-enabled {
  background-color: #6c757d !important;
  cursor: pointer !important;
  pointer-events: auto !important;
}

.btn-enabled:hover {
  background-color: #5a6268 !important;
}

.links {
  margin-top: 1rem;
  text-align: center;
}

.links a {
  color: #007bff;
  text-decoration: none;
  margin: 0 0.5rem;
  cursor: pointer;
}

.links a:hover {
  text-decoration: underline;
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.input-type {
  font-size: 0.875rem;
  color: #6c757d;
  margin-left: 0.5rem;
}

/* 移除舊的響應式樣式，因為現在是 modal */
@media (max-width: 768px) {
  .login-modal {
    padding-top: 5vh; /* 在小屏幕上減少頂部間距 */
  }
  
  .form-container {
    padding: 1.5rem;
    margin: 0 1rem;
  }
}

/* 超小屏幕設備進一步優化 */
@media (max-width: 480px) {
  .login-modal {
    padding-top: 2vh; /* 在超小屏幕上進一步減少頂部間距 */
    align-items: flex-start;
  }
  
  .form-container {
    padding: 1rem;
    margin: 0 0.5rem;
    max-width: 95%;
  }
  
  h2 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
  
  .form-group {
    margin-bottom: 0.75rem;
  }
  
  input {
    padding: 0.6rem;
  }
  
  .btn-primary {
    padding: 0.6rem;
  }
}

/* 添加關閉按鈕樣式 */
.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  z-index: 10;
}

.close-btn:hover {
  color: #000;
}

/* 添加條款和隱私政策連結的樣式 */
.terms-privacy {
  font-size: 0.8rem;
  color: #666;
  text-align: center;
  margin: 10px 0;
  line-height: 1.4;
}

.terms-privacy a {
  color: #419dda;
  text-decoration: none;
  cursor: pointer;
  position: relative;
  padding-bottom: 1px;
}

.terms-privacy a::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 1px;
  bottom: 0;
  left: 0;
  background-color: #419dda;
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.terms-privacy a:hover::after {
  transform: scaleX(1);
}

/* 密碼輸入框容器樣式 */
.password-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

/* 密碼顯示切換按鈕樣式 */
.toggle-password {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
}

.toggle-password:hover {
  color: #333;
}

/* 確保密碼輸入框有足夠的右側內邊距 */
.password-input-container input {
  padding-right: 40px;
}


/* 統一認證系統樣式 */
.unified-auth-message {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  color: white;
  margin: 20px 0;
}

.message-icon {
  font-size: 48px;
  margin-bottom: 15px;
  opacity: 0.9;
}

.message-content h3 {
  margin: 0 0 10px 0;
  font-size: 24px;
  font-weight: 600;
}

.message-content p {
  margin: 0 0 20px 0;
  font-size: 16px;
  opacity: 0.9;
}

.redirect-info {
  text-align: left;
  margin: 20px 0;
  background: rgba(255, 255, 255, 0.1);
  padding: 15px;
  border-radius: 8px;
}

.redirect-info p {
  margin: 0 0 10px 0;
  font-weight: 600;
}

.redirect-info ul {
  margin: 0;
  padding-left: 20px;
}

.redirect-info li {
  margin: 5px 0;
  font-size: 14px;
}

.btn-primary {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;
}

.btn-primary:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
}

.btn-primary i {
  margin-right: 8px;
}

/* Google 登入按鈕樣式 */
.btn-google {
  width: 100%;
  padding: 0.75rem;
  background-color: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.btn-google:hover {
  background-color: #3367d6;
}

.btn-google i {
  font-size: 1.1rem;
}

/* 分隔線樣式 */
.divider {
  display: flex;
  align-items: center;
  margin: 1rem 0;
  color: #666;
  font-size: 0.9rem;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #ddd;
}

.divider span {
  padding: 0 1rem;
  background: white;
}
</style>
